import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Icon, CtaButton } from "./primitives";
import { PROCESS } from "../content/site";
import { CASE_STUDIES } from "../content/caseStudies";
import { track, EVENTS } from "../lib/analytics";
import { Photo } from "./Photo";
import { SERVICE_PHOTOS, CASE_PHOTOS } from "../content/photos";

export function ServiceCard({ s, onOpen, dark, index = 0 }) {
  return (
    <article className={`bento ${dark ? "dark" : "light-card"} reveal p-7 flex flex-col group`} style={{ transitionDelay: `${index * 60}ms` }} tabIndex={0} data-testid={`service-card-${s.slug}`}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(s); } }} aria-label={`${s.title} service`}>
      <div className="card-photo"><Photo name={SERVICE_PHOTOS[s.slug]} fill parallax={0} treat="default" particles sizes="(max-width: 768px) 100vw, 33vw" /></div>
      <div className="relative flex flex-col flex-1">
      <div className={`h-12 w-12 rounded-xl grid place-items-center ${dark ? "bg-[#1A6EFF]/20 text-[#00D4FF]" : "bg-[#F0F4FF] text-[#1A6EFF]"}`}><Icon name={s.icon} className="h-6 w-6" /></div>
      <h3 className={`mt-6 font-display text-xl font-bold ${dark ? "text-white" : "text-[#0A1628]"}`}>{s.title}</h3>
      <p className={`mt-3 text-sm leading-relaxed flex-1 ${dark ? "text-white/60" : "text-muted-foreground"}`}>{s.short}</p>
      <div className="mt-5 flex flex-wrap gap-1.5">
        {s.sub.slice(0, 3).map((x) => <span key={x} className={`text-[11px] px-2 py-0.5 rounded-full ${dark ? "bg-white/5 text-white/60" : "bg-[#F0F4FF] text-[#0A1628]/70"}`}>{x}</span>)}
        {s.sub.length > 3 && <span className={`text-[11px] px-2 py-0.5 rounded-full ${dark ? "text-white/40" : "text-muted-foreground"}`}>+{s.sub.length - 3}</span>}
      </div>
      <button onClick={() => onOpen(s)} data-testid={`service-explore-${s.slug}`} className={`mt-6 inline-flex items-center gap-1.5 text-sm font-semibold ${dark ? "text-[#00D4FF]" : "text-[#1A6EFF]"} group-hover:gap-2.5 transition-[gap] duration-300 outline-none focus-visible:underline`}>
        Explore <ArrowRight className="h-4 w-4" />
      </button>
      </div>
    </article>
  );
}

export function ServiceModal({ service, onClose }) {
  const cs = service ? CASE_STUDIES.find((c) => c.category === service.caseCategory) : null;
  return (
    <Dialog open={!!service} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden rounded-2xl" data-testid="service-modal">
        {service && (
          <>
            <DialogHeader className="bg-navy text-white p-8 text-left">
              <div className="h-12 w-12 rounded-xl bg-[#1A6EFF]/25 text-[#00D4FF] grid place-items-center"><Icon name={service.icon} className="h-6 w-6" /></div>
              <DialogTitle className="mt-4 font-display text-2xl md:text-3xl font-bold">{service.title}</DialogTitle>
              <DialogDescription className="text-white/65 text-base">{service.short}</DialogDescription>
            </DialogHeader>
            <Tabs defaultValue="sub" className="p-6" onValueChange={(v) => track(EVENTS.SERVICE_INTERACTION, { service: service.slug, tab: v })}>
              <TabsList className="grid grid-cols-3 h-11 rounded-full bg-[#F0F4FF]">
                <TabsTrigger value="sub" className="nav-label rounded-full" data-testid="service-tab-sub">Sub-services</TabsTrigger>
                <TabsTrigger value="process" className="nav-label rounded-full" data-testid="service-tab-process">Our process</TabsTrigger>
                <TabsTrigger value="case" className="nav-label rounded-full" data-testid="service-tab-case">Case study</TabsTrigger>
              </TabsList>
              <TabsContent value="sub" className="mt-6 grid sm:grid-cols-2 gap-2">
                {service.sub.map((x) => <div key={x} className="rounded-lg border border-border bg-white px-4 py-3 text-sm flex items-center gap-2"><span className="h-1.5 w-1.5 rounded-full bg-[#1A6EFF]" />{x}</div>)}
              </TabsContent>
              <TabsContent value="process" className="mt-6 space-y-3">
                {PROCESS.map((p) => (
                  <div key={p.n} className="flex gap-4"><span className="eyebrow text-[#1A6EFF] mt-1 w-6">{p.n}</span><div><div className="font-semibold text-sm">{p.title}</div><div className="text-sm text-muted-foreground">{p.desc}</div></div></div>
                ))}
              </TabsContent>
              <TabsContent value="case" className="mt-6">
                {cs ? (
                  <div className="rounded-xl border border-border p-5 bg-[#F8FAFF]">
                    <div className="flex items-center gap-2"><Badge variant="outline" className="eyebrow">{cs.category}</Badge>{cs.placeholder && <Badge className="eyebrow bg-amber-100 text-amber-800 hover:bg-amber-100">Placeholder</Badge>}</div>
                    <div className="mt-3 font-display font-bold text-lg">{cs.title}</div>
                    <p className="text-sm text-muted-foreground mt-1">{cs.client}</p>
                    <p className="text-sm mt-3"><span className="font-semibold">Challenge.</span> {cs.challenge}</p>
                    <p className="text-sm mt-2"><span className="font-semibold">Solution.</span> {cs.solution}</p>
                    <Link to={`/case-studies?category=${cs.category}`} className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#1A6EFF]">View all {cs.category} case studies <ArrowUpRight className="h-4 w-4" /></Link>
                  </div>
                ) : <p className="text-sm text-muted-foreground">Case study coming soon.</p>}
              </TabsContent>
              <div className="mt-8 flex flex-wrap gap-3">
                <CtaButton to="/contact" testId="service-modal-cta" label={`Consult ${service.title}`}>Book a Consultation <ArrowRight className="ml-2 h-4 w-4" /></CtaButton>
                <CtaButton to={`/services#${service.slug}`} variant="ghost" onClick={onClose}>Service details</CtaButton>
              </div>
            </Tabs>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function ProcessTimeline({ dark }) {
  return (
    <ol className="relative grid md:grid-cols-3 lg:grid-cols-6 gap-6" aria-label="How we work">
      <div className={`hidden lg:block absolute top-6 left-[8%] right-[8%] h-px ${dark ? "bg-gradient-to-r from-[#1A6EFF] via-[#00D4FF] to-[#1A6EFF]" : "bg-gradient-to-r from-[#1A6EFF]/30 via-[#1A6EFF] to-[#1A6EFF]/30"}`} aria-hidden="true" />
      {PROCESS.map((p, i) => (
        <li key={p.n} className="reveal relative" style={{ transitionDelay: `${i * 90}ms` }} data-testid={`process-step-${p.title.toLowerCase()}`}>
          <div className={`relative z-10 h-12 w-12 rounded-full grid place-items-center font-mono text-sm font-bold ${dark ? "bg-navy border-2 border-[#00D4FF] text-[#00D4FF] glow-cyan" : "bg-white border-2 border-[#1A6EFF] text-[#1A6EFF] glow-blue"}`}>{p.n}</div>
          <h3 className={`mt-5 font-display font-bold text-lg ${dark ? "text-white" : "text-[#0A1628]"}`}>{p.title}</h3>
          <p className={`mt-2 text-sm leading-relaxed ${dark ? "text-white/60" : "text-muted-foreground"}`}>{p.desc}</p>
        </li>
      ))}
    </ol>
  );
}

export function CaseStudyCard({ c, index = 0 }) {
  return (
    <article className="bento reveal p-5 flex flex-col" style={{ transitionDelay: `${index * 60}ms` }} data-testid={`case-card-${c.id}`}>
      <Link to="/contact" onClick={() => track(EVENTS.CASE_STUDY_VIEW, { id: c.id })} className="case-media block" aria-label={`${c.title} — view case study`}>
        <Photo name={CASE_PHOTOS[c.category]} ratio="16/9" parallax={6} treat={null} sizes="(max-width: 768px) 100vw, 33vw">
          <div className="case-overlay" aria-hidden="true" />
          <div className="absolute top-3 left-3 flex items-center gap-2">
            <Badge className="eyebrow bg-white/10 text-white border border-white/20 backdrop-blur hover:bg-white/10">{c.category}</Badge>
            {c.placeholder && <Badge className="eyebrow bg-amber-400/90 text-[#0A1628] hover:bg-amber-400/90">Placeholder</Badge>}
          </div>
          <div className="case-caption">
            <div className="font-display text-lg font-bold leading-tight">{c.title}</div>
            <div className="text-xs text-white/70 mt-0.5">{c.client}</div>
            <span className="case-view">View case study <ArrowRight className="h-3.5 w-3.5" /></span>
          </div>
        </Photo>
      </Link>
      <dl className="mt-5 px-2 space-y-3 text-sm flex-1">
        <div><dt className="eyebrow text-[#0A1628]/50">Challenge</dt><dd className="mt-1 leading-relaxed">{c.challenge}</dd></div>
        <div><dt className="eyebrow text-[#0A1628]/50">Solution</dt><dd className="mt-1 leading-relaxed">{c.solution}</dd></div>
        <div><dt className="eyebrow text-[#0A1628]/50">Outcome</dt>
          {c.metrics?.length ? (
            <dd className="mt-2 grid grid-cols-3 gap-2" data-testid={`case-metrics-${c.id}`}>
              {c.metrics.slice(0, 3).map((m) => <div key={m.label} className="rounded-xl bg-[#F0F4FF] px-3 py-2.5"><div className="font-display text-xl font-extrabold text-[#1A6EFF] leading-none">{m.value}</div><div className="mt-1 text-[11px] leading-snug text-[#0A1628]/70">{m.label}</div></div>)}
            </dd>
          ) : null}
          <dd className={`mt-1 leading-relaxed ${c.placeholder ? "italic text-muted-foreground" : ""}`}>{c.outcome}</dd>
          {c.quote && <dd className="mt-3 border-l-2 border-[#00D4FF] pl-3 text-[13px] leading-relaxed" data-testid={`case-quote-${c.id}`}>“{c.quote.text}”<div className="mt-1 not-italic text-xs font-semibold text-[#0A1628]/70">{c.quote.name}{c.quote.role ? ` · ${c.quote.role}` : ""}</div></dd>}
        </div>
      </dl>
      <div className="mt-5 px-2 flex flex-wrap gap-1.5">{c.tech.map((t) => <span key={t} className="text-[11px] px-2 py-0.5 rounded-full bg-[#F0F4FF] text-[#0A1628]/70">{t}</span>)}</div>
      <Link to="/contact" onClick={() => track(EVENTS.CASE_STUDY_VIEW, { id: c.id })} className="mt-5 px-2 pb-1 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1A6EFF] hover:gap-2.5 transition-[gap]">Discuss a similar outcome <ArrowRight className="h-4 w-4" /></Link>
    </article>
  );
}
